import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { InvoiceStatusFilter } from '@portal-contract';
import { api } from './client';

/** React Query hooks over the typed client. Every key starts with the customer id so a switch never shows stale data. */
export const keys = {
  overview: (cid: string) => ['overview', cid] as const,
  invoices: (cid: string, status: InvoiceStatusFilter, q: string, from: string, to: string) => ['invoices', cid, status, q, from, to] as const,
  invoice: (cid: string, iid: string) => ['invoice', cid, iid] as const,
  payments: (cid: string) => ['payments', cid] as const,
  statement: (cid: string, from: string | undefined, to: string | undefined) => ['statement', cid, from ?? '', to ?? ''] as const,
};

export const useOverview = (cid: string) =>
  useQuery({ queryKey: keys.overview(cid), queryFn: ({ signal }) => api.overview(cid, signal) });

export const useInvoice = (cid: string, iid: string) =>
  useQuery({ queryKey: keys.invoice(cid, iid), queryFn: ({ signal }) => api.invoice(cid, iid, signal) });

export function useInvoices(cid: string, f: { status: InvoiceStatusFilter; q: string; from: string; to: string }) {
  return useInfiniteQuery({
    queryKey: keys.invoices(cid, f.status, f.q, f.from, f.to),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api.invoices(cid, { status: f.status, ...(f.q ? { q: f.q } : {}), ...(f.from ? { from: f.from } : {}), ...(f.to ? { to: f.to } : {}), ...(pageParam ? { cursor: pageParam } : {}) }, signal),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function usePayments(cid: string) {
  return useInfiniteQuery({
    queryKey: keys.payments(cid),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => api.payments(cid, pageParam ? { cursor: pageParam } : {}, signal),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** `from` and `to` undefined asks for the server default period (first of the month 3 months back to today). */
export const useStatement = (cid: string, from: string | undefined, to: string | undefined, enabled = true) =>
  useQuery({
    queryKey: keys.statement(cid, from, to),
    queryFn: ({ signal }) => api.statement(cid, { ...(from ? { from } : {}), ...(to ? { to } : {}) }, signal),
    enabled,
    placeholderData: keepPreviousData,
  });
